import { TFunction } from 'next-i18next';
import useResizeObserver from 'use-resize-observer';
import {
  Dispatch, RefObject, SetStateAction, useCallback, useState,
} from 'react';
import { InfiniteList, PageCounter, Button } from '@components/common';
import { PaginationPageType } from '@type/swr';
import { McTalentCardMemo, McTalentCardFormProps } from '@components/templates/employer/applicant-list/flow-mc/card/McTalentCard';
import { McApplicantsPage, McApplicantCardData } from '@components/templates/employer/applicant-list/flow-mc/types';
import { McPhoneApplicantPreviewModal } from '@components/templates/employer/applicant-list/flow-mc/partials/McPhoneApplicantPreviewModal';

interface McApplicantListProps {
  data?: McApplicantsPage[];
  size: number;
  setSize: (size: number | ((_size: number) => number)) => Promise<unknown[] | undefined>;
  isLoading?: boolean;
  isValidating?: boolean;
  sizeAsRef?: RefObject<number>;
  showLoadMore?: boolean;
  isEmpty?: boolean;
  jobSlug?: string;
  jobInfo?: ApiSchema<'ListReadJob'>;
  t: TFunction;
  setCurrentPage: Dispatch<SetStateAction<PaginationPageType<'page'>>>;
  applicant?: McTalentCardFormProps;
  locale: Locale;
  statusContext?: string | number;
}

export const McApplicantList = (props: McApplicantListProps) => {
  const {
    data,
    size,
    setSize,
    isLoading,
    isValidating,
    sizeAsRef,
    showLoadMore,
    isEmpty,
    jobSlug,
    jobInfo,
    t,
    setCurrentPage,
    applicant,
    locale,
    statusContext,
  } = props;

  const [selectedPhoneApplicant, setSelectedPhoneApplicant] = useState<McApplicantCardData | undefined>(undefined);

  const { ref: parentRef, width: parentWidth } = useResizeObserver<HTMLDivElement>({
    round: Math.floor,
  });

  const closePhoneApplicantPreview = useCallback(() => {
    setSelectedPhoneApplicant(undefined);
  }, []);

  const isRefreshing = isValidating && !isLoading;
  const containerClasses = `my-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6 xl:my-6 ${isRefreshing ? 'opacity-80 saturate-50' : ''}`;
  const coverClasses = 'absolute inset-0 z-10 cursor-wait';

  return (
    <>
      <InfiniteList
        autoload={3}
        infiniteProps={{
          setSize,
          isEmpty,
          showLoadMore,
          sizeAsRef,
          isLoading,
        }}
        isEmptyLabels={{
          isEmptyTitle: 'no_results',
          isEmptyText: 'cv.search.message.no_result',
        }}
        renderList={() => (
          <div ref={parentRef} className={containerClasses}>
            {isRefreshing && <div className={coverClasses} />}

            {data?.map((pageGroup) => {
              const viewportPage = pageGroup.page ?? 1;

              return (
                <PageCounter
                  key={`page-${viewportPage}`}
                  pageGroup={pageGroup}
                  currentPage={viewportPage}
                  callback={setCurrentPage}
                  parentWidth={parentWidth}
                  paginationType="page"
                >
                  {pageGroup.items?.map((item) => (
                    <McTalentCardMemo
                      key={item.id}
                      cardData={item}
                      jobSlug={jobSlug}
                      jobInfo={jobInfo}
                      applicant={applicant}
                      showActions={false}
                      openPhoneApplicantPreview={setSelectedPhoneApplicant}
                      locale={locale}
                      statusContext={statusContext}
                    />
                  ))}
                </PageCounter>
              );
            })}
          </div>
        )}
        renderButton={() => (
          <div className="my-8 text-center">
            <Button
              isLoading={isLoading}
              rounding="full"
              color="secondary"
              onClick={() => { void setSize(size + 1); }}
            >
              {t('dashboard.label.notification_show')}
            </Button>
          </div>
        )}
      />

      <McPhoneApplicantPreviewModal
        applicant={selectedPhoneApplicant}
        isOpen={Boolean(selectedPhoneApplicant)}
        onClose={closePhoneApplicantPreview}
      />
    </>
  );
};
